import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useGenerateExecutionPlan,
  useGenerateKeywordMaster,
  useGeneratePainPointMap,
  useGeneratePersonaResearch,
  useGeneratePositioningAngles,
  useGenerateStrategyOverlay,
  useResearchPackage,
  useStartPageProduction,
} from "../hooks/useMarketResearch";
import type {
  ResearchPackageSourceValidation,
  ResearchPackageStageState,
} from "../hooks/useMarketResearch";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { apiUrl } from "../lib/api";

function stageLabel(stage: string): string {
  return stage.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

const ALL_STAGES = [
  "market_research_report",
  "keyword_master",
  "strategy_overlay",
  "execution_plan",
  "persona_research",
  "pain_point_map",
  "positioning_angles",
  "page_production",
] as const;

function defaultStage(stage: (typeof ALL_STAGES)[number]): ResearchPackageStageState {
  return {
    stage,
    status: "not_started",
    document_key: null,
    artifact_key: null,
    workflow_instance_id: null,
    pending_gate_id: null,
  };
}

function normalizeStages(stages: ResearchPackageStageState[]): ResearchPackageStageState[] {
  const stageMap = new Map(stages.map((s) => [s.stage, s]));
  const known = ALL_STAGES.map((stageKey) => stageMap.get(stageKey) ?? defaultStage(stageKey));
  const unknown = stages.filter((s) => !(ALL_STAGES as readonly string[]).includes(s.stage));
  return [...known, ...unknown];
}

function validationBadgeClass(ready: boolean): string {
  return ready
    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
    : "border-amber-200 bg-amber-50 text-amber-800";
}

function validationIssueLabel(kind: ResearchPackageSourceValidation["issues"][number]["kind"]): string {
  switch (kind) {
    case "report":
      return "Report";
    case "retrieval":
      return "Retrieval";
    case "source":
      return "Source";
    default:
      return "Validation";
  }
}

function packageDocKey(stages: ResearchPackageStageState[], stageName: ResearchPackageStageState["stage"]): string | null {
  return stages.find((stage) => stage.stage === stageName)?.document_key ?? null;
}

function packageStage(
  stages: ResearchPackageStageState[],
  stageName: ResearchPackageStageState["stage"],
): ResearchPackageStageState | null {
  return stages.find((stage) => stage.stage === stageName) ?? null;
}

function packageApprovedDocKey(
  stages: ResearchPackageStageState[],
  stageName: ResearchPackageStageState["stage"],
): string | null {
  const stage = packageStage(stages, stageName);
  return stage?.status === "approved" ? stage.document_key ?? null : null;
}

export function BrandResearchPackage() {
  const { brandId, packageId } = useParams<{ brandId: string; packageId: string }>();
  const { data, isLoading, isError, error } = useResearchPackage(brandId, packageId);
  const generateKeywordMaster = useGenerateKeywordMaster(brandId, packageId);
  const generateStrategyOverlay = useGenerateStrategyOverlay(brandId, packageId);
  const generateExecutionPlan = useGenerateExecutionPlan(brandId, packageId);
  const generatePersonaResearch = useGeneratePersonaResearch(brandId, packageId);
  const generatePainPointMap = useGeneratePainPointMap(brandId, packageId);
  const generatePositioningAngles = useGeneratePositioningAngles(brandId, packageId);
  const startPageProduction = useStartPageProduction(brandId, packageId);
  const [keywordMasterError, setKeywordMasterError] = useState<string | null>(null);
  const [strategyOverlayError, setStrategyOverlayError] = useState<string | null>(null);
  const [executionPlanError, setExecutionPlanError] = useState<string | null>(null);
  const [personaError, setPersonaError] = useState<string | null>(null);
  const [painError, setPainError] = useState<string | null>(null);
  const [positioningError, setPositioningError] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [pageKeyword, setPageKeyword] = useState("");
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportPending, setExportPending] = useState(false);
  const pkg = data?.data;
  const normalizedStages = pkg ? normalizeStages(pkg.stages) : [];
  const sourceValidation = pkg?.source_validation ?? null;
  const sourceValidated = sourceValidation?.ready ?? true;

  const keywordMasterDoc = packageDocKey(normalizedStages, "keyword_master");
  const strategyOverlayDoc = packageDocKey(normalizedStages, "strategy_overlay");
  const executionPlanDoc = packageDocKey(normalizedStages, "execution_plan");
  const personaDoc = packageDocKey(normalizedStages, "persona_research");
  const painDoc = packageDocKey(normalizedStages, "pain_point_map");
  const positioningDoc = packageDocKey(normalizedStages, "positioning_angles");
  const keywordMasterApprovedDoc = packageApprovedDocKey(normalizedStages, "keyword_master");
  const strategyOverlayApprovedDoc = packageApprovedDocKey(normalizedStages, "strategy_overlay");
  const executionPlanApprovedDoc = packageApprovedDocKey(normalizedStages, "execution_plan");
  const personaApprovedDoc = packageApprovedDocKey(normalizedStages, "persona_research");
  const painApprovedDoc = packageApprovedDocKey(normalizedStages, "pain_point_map");
  const positioningApprovedDoc = packageApprovedDocKey(normalizedStages, "positioning_angles");

  const nextOutputLabel = (() => {
    if (!pkg) return "Keyword Master";
    if (!keywordMasterApprovedDoc) return "Keyword Master";
    if (!strategyOverlayApprovedDoc) return "Strategy Overlay";
    if (!executionPlanApprovedDoc) return "Execution Plan";
    if (!personaApprovedDoc) return "Persona Research";
    if (!painApprovedDoc) return "Pain Point Map";
    if (!positioningApprovedDoc) return "Positioning And Angles";
    const pageProduction = normalizedStages.find((s) => s.stage === "page_production");
    if (pageProduction?.status !== "approved") return "First Page Asset";
    return "Package chain complete";
  })();

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (isError || !pkg) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        {error instanceof Error ? error.message : "Failed to load research package"}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <WorkspaceHeader
        eyebrow="Research Package"
        title="Research Package"
        description={`Package ${pkg.id} from run ${pkg.source_run_id}. This document chain tracks the progression from the source research document through strategy validation and the first page asset.`}
        breadcrumbs={[
          { label: "Actions", href: "/tasks" },
          { label: "Research Documents", href: `/brand/${brandId}/research` },
          { label: "Research Package" },
        ]}
        badges={(
          <>
            <span className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">
              {stageLabel(pkg.status)}
            </span>
            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
              {normalizedStages.length} stages
            </span>
          </>
        )}
        metadata={(
          <>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              Source run {pkg.source_run_id}
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              Next output: {nextOutputLabel}
            </span>
          </>
        )}
      />

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Package status</p>
          <p className="mt-2 text-lg font-semibold text-slate-900">{stageLabel(pkg.status)}</p>
          <p className="mt-2 text-xs text-slate-500">Artifact key: {pkg.market_research_artifact_key}</p>
        </div>
        <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Next intended output</p>
          <p className="mt-2 text-lg font-semibold text-slate-900">{nextOutputLabel}</p>
          <p className="mt-2 text-xs text-slate-500">
            Advance from research package outputs into validated strategy artifacts, then generate the first ThinkBrowse page.
          </p>
        </div>
      </div>

      <div className={`rounded-[20px] border p-4 shadow-sm ${validationBadgeClass(sourceValidated)}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide">Source validation</p>
            <p className="mt-2 text-lg font-semibold">
              {sourceValidated ? "Validated for strategy generation" : "Validation blockers"}
            </p>
          </div>
          <div className="text-right text-xs">
            {sourceValidation?.workflow_run_id ? (
              <p>Workflow run: {sourceValidation.workflow_run_id}</p>
            ) : null}
            <p>Findings: {sourceValidation?.findings_count ?? 0}</p>
          </div>
        </div>
        <p className="mt-2 text-sm">
          {sourceValidated
            ? "This package can generate downstream strategy documents because the source research includes a report and complete retrieval/source provenance."
            : "Complete source validation first. Strategy documents stay disabled until the source market-research artifact has a report, query context, and source provenance for every finding."}
        </p>
        {(sourceValidation?.issues.length ?? 0) > 0 ? (
          <ul className="mt-3 space-y-2 text-sm">
            {sourceValidation?.issues.map((issue) => (
              <li key={`${issue.kind}:${issue.code}:${issue.finding_title ?? issue.message}`}>
                <span className="font-medium">{validationIssueLabel(issue.kind)}:</span> {issue.message}
                {issue.finding_title ? ` (${issue.finding_title})` : ""}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="grid gap-3">
        {normalizedStages.map((stage) => (
          <div key={stage.stage} className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-slate-900">{stageLabel(stage.stage)}</h2>
                <p className="mt-1 text-xs text-slate-500">Status: {stageLabel(stage.status)}</p>
              </div>
              <div className="text-right text-xs text-slate-500">
                {stage.document_key ? <p>Document: {stage.document_key}</p> : null}
                {stage.artifact_key ? <p>Artifact: {stage.artifact_key}</p> : null}
                {stage.workflow_instance_id ? <p>Workflow: {stage.workflow_instance_id}</p> : null}
              </div>
            </div>

            {stage.pending_gate_id ? (
              <div className="mt-3">
                <Link
                  to={`/gates/${encodeURIComponent(stage.pending_gate_id)}`}
                  className="text-sm font-medium text-amber-700 hover:text-amber-900"
                >
                  Open review gate
                </Link>
              </div>
            ) : null}

            {stage.stage === "keyword_master" ? (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    void (async () => {
                      setKeywordMasterError(null);
                      try {
                        await generateKeywordMaster.mutateAsync();
                      } catch (mutationError) {
                        setKeywordMasterError(
                          mutationError instanceof Error
                            ? mutationError.message
                            : "Could not generate the keyword master.",
                        );
                      }
                    })();
                  }}
                  disabled={generateKeywordMaster.isPending || !sourceValidated}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {generateKeywordMaster.isPending
                    ? "Generating…"
                    : stage.document_key
                      ? "Regenerate Keyword Master"
                      : "Generate Keyword Master"}
                </button>
                {stage.document_key ? (
                  <Link to={`/outputs/brand_document/${encodeURIComponent(stage.document_key)}`} className="text-sm font-medium text-brand-700 hover:text-brand-900">
                    Open output document
                  </Link>
                ) : null}
                {keywordMasterError ? (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{keywordMasterError}</p>
                ) : null}
              </div>
            ) : null}

            {stage.stage === "strategy_overlay" ? (
              <div className="mt-4 space-y-2">
                {!sourceValidated ? <p className="text-xs text-amber-700">Resolve source validation blockers before generating strategy documents.</p> : null}
                {!keywordMasterDoc ? <p className="text-xs text-amber-700">Generate the keyword master before running this stage.</p> : null}
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      void (async () => {
                        setStrategyOverlayError(null);
                        try {
                          await generateStrategyOverlay.mutateAsync();
                        } catch (mutationError) {
                          setStrategyOverlayError(
                            mutationError instanceof Error
                              ? mutationError.message
                              : "Could not generate the strategy overlay.",
                          );
                        }
                      })();
                    }}
                    disabled={generateStrategyOverlay.isPending || !keywordMasterDoc || !sourceValidated}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {generateStrategyOverlay.isPending
                      ? "Generating…"
                      : stage.document_key
                        ? "Regenerate Strategy Overlay"
                        : "Generate Strategy Overlay"}
                  </button>
                  {stage.document_key ? (
                    <Link to={`/outputs/brand_document/${encodeURIComponent(stage.document_key)}`} className="text-sm font-medium text-brand-700 hover:text-brand-900">
                      Open output document
                    </Link>
                  ) : null}
                </div>
                {strategyOverlayError ? (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{strategyOverlayError}</p>
                ) : null}
              </div>
            ) : null}

            {stage.stage === "execution_plan" ? (
              <div className="mt-4 space-y-2">
                {!sourceValidated ? <p className="text-xs text-amber-700">Resolve source validation blockers before generating strategy documents.</p> : null}
                {!keywordMasterDoc ? <p className="text-xs text-amber-700">Generate the keyword master before running this stage.</p> : null}
                {!strategyOverlayDoc ? <p className="text-xs text-amber-700">Generate the strategy overlay before running this stage.</p> : null}
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      void (async () => {
                        setExecutionPlanError(null);
                        try {
                          await generateExecutionPlan.mutateAsync();
                        } catch (mutationError) {
                          setExecutionPlanError(
                            mutationError instanceof Error
                              ? mutationError.message
                              : "Could not generate the execution plan.",
                          );
                        }
                      })();
                    }}
                    disabled={generateExecutionPlan.isPending || !keywordMasterDoc || !strategyOverlayDoc || !sourceValidated}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {generateExecutionPlan.isPending
                      ? "Generating…"
                      : stage.document_key
                        ? "Regenerate Execution Plan"
                        : "Generate Execution Plan"}
                  </button>
                  {stage.document_key ? (
                    <Link to={`/outputs/brand_document/${encodeURIComponent(stage.document_key)}`} className="text-sm font-medium text-brand-700 hover:text-brand-900">
                      Open output document
                    </Link>
                  ) : null}
                </div>
                {executionPlanError ? (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{executionPlanError}</p>
                ) : null}
              </div>
            ) : null}

            {stage.stage === "persona_research" ? (
              <div className="mt-4 space-y-2">
                {!executionPlanApprovedDoc ? <p className="text-xs text-amber-700">Approve the execution plan before running persona research.</p> : null}
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      void (async () => {
                        setPersonaError(null);
                        try {
                          await generatePersonaResearch.mutateAsync();
                        } catch (mutationError) {
                          setPersonaError(
                            mutationError instanceof Error
                              ? mutationError.message
                              : "Could not generate persona research.",
                          );
                        }
                      })();
                    }}
                    disabled={generatePersonaResearch.isPending || !executionPlanApprovedDoc || !sourceValidated}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {generatePersonaResearch.isPending
                      ? "Generating…"
                      : stage.document_key
                        ? "Regenerate Persona Research"
                        : "Generate Persona Research"}
                  </button>
                  {stage.document_key ? (
                    <Link to={`/outputs/brand_document/${encodeURIComponent(stage.document_key)}`} className="text-sm font-medium text-brand-700 hover:text-brand-900">
                      Open output document
                    </Link>
                  ) : null}
                </div>
                {personaError ? (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{personaError}</p>
                ) : null}
              </div>
            ) : null}

            {stage.stage === "pain_point_map" ? (
              <div className="mt-4 space-y-2">
                {!personaApprovedDoc ? <p className="text-xs text-amber-700">Approve persona research before running the pain-point map.</p> : null}
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      void (async () => {
                        setPainError(null);
                        try {
                          await generatePainPointMap.mutateAsync();
                        } catch (mutationError) {
                          setPainError(
                            mutationError instanceof Error
                              ? mutationError.message
                              : "Could not generate the pain-point map.",
                          );
                        }
                      })();
                    }}
                    disabled={generatePainPointMap.isPending || !personaApprovedDoc || !sourceValidated}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {generatePainPointMap.isPending
                      ? "Generating…"
                      : stage.document_key
                        ? "Regenerate Pain Point Map"
                        : "Generate Pain Point Map"}
                  </button>
                  {stage.document_key ? (
                    <Link to={`/outputs/brand_document/${encodeURIComponent(stage.document_key)}`} className="text-sm font-medium text-brand-700 hover:text-brand-900">
                      Open output document
                    </Link>
                  ) : null}
                </div>
                {painError ? (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{painError}</p>
                ) : null}
              </div>
            ) : null}

            {stage.stage === "positioning_angles" ? (
              <div className="mt-4 space-y-2">
                {!personaApprovedDoc ? <p className="text-xs text-amber-700">Approve persona research before running positioning.</p> : null}
                {!painApprovedDoc ? <p className="text-xs text-amber-700">Approve the pain-point map before running positioning.</p> : null}
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      void (async () => {
                        setPositioningError(null);
                        try {
                          await generatePositioningAngles.mutateAsync();
                        } catch (mutationError) {
                          setPositioningError(
                            mutationError instanceof Error
                              ? mutationError.message
                              : "Could not generate positioning.",
                          );
                        }
                      })();
                    }}
                    disabled={generatePositioningAngles.isPending || !personaApprovedDoc || !painApprovedDoc || !sourceValidated}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {generatePositioningAngles.isPending
                      ? "Generating…"
                      : stage.document_key
                        ? "Regenerate Positioning And Angles"
                        : "Generate Positioning And Angles"}
                  </button>
                  {stage.document_key ? (
                    <Link to={`/outputs/brand_document/${encodeURIComponent(stage.document_key)}`} className="text-sm font-medium text-brand-700 hover:text-brand-900">
                      Open output document
                    </Link>
                  ) : null}
                </div>
                {positioningError ? (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{positioningError}</p>
                ) : null}
              </div>
            ) : null}

            {stage.stage === "page_production" ? (
              <div className="mt-4 space-y-3">
                {!positioningApprovedDoc ? <p className="text-xs text-amber-700">Approve positioning before starting page production.</p> : null}
                <label className="block text-xs font-medium text-slate-600">
                  Target keyword
                  <input
                    type="text"
                    value={pageKeyword}
                    onChange={(event) => setPageKeyword(event.target.value)}
                    placeholder="Enter the target keyword for the first page asset"
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900"
                  />
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      void (async () => {
                        setPageError(null);
                        try {
                          await startPageProduction.mutateAsync({
                            targetKeyword: pageKeyword,
                            pageType: "comparison",
                            pageIntent: "commercial",
                          });
                        } catch (mutationError) {
                          setPageError(
                            mutationError instanceof Error
                              ? mutationError.message
                              : "Could not start page production.",
                          );
                        }
                      })();
                    }}
                    disabled={startPageProduction.isPending || !positioningApprovedDoc || !pageKeyword.trim()}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {startPageProduction.isPending
                      ? "Starting…"
                      : stage.document_key
                        ? "Regenerate First Page Asset"
                        : "Generate First Page Asset"}
                  </button>
                  {stage.document_key ? (
                    <Link to={`/outputs/brand_document/${encodeURIComponent(stage.document_key)}`} className="text-sm font-medium text-brand-700 hover:text-brand-900">
                      Open latest page artifact
                    </Link>
                  ) : null}
                </div>
                {pageError ? (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{pageError}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-900">Package Export</h2>
        <p className="mt-1 text-xs text-slate-500">
          Download a bundled markdown export of all completed research artifacts.
        </p>
        <div className="mt-4">
          <button
            type="button"
            disabled={exportPending}
            onClick={() => {
              void (async () => {
                setExportError(null);
                setExportPending(true);
                try {
                  const response = await fetch(
                    apiUrl(`/api/brands/${brandId}/research/packages/${packageId}/export`),
                    { credentials: "include" },
                  );
                  if (response.status === 403) {
                    setExportError("You do not have permission to export this package.");
                    return;
                  }
                  if (response.status === 404) {
                    setExportError("Package not found.");
                    return;
                  }
                  if (!response.ok) {
                    setExportError("Export failed. Please try again.");
                    return;
                  }
                  const blob = await response.blob();
                  const url = URL.createObjectURL(blob);
                  const anchor = document.createElement("a");
                  anchor.href = url;
                  anchor.download = `research-package-${packageId ?? "export"}.md`;
                  document.body.appendChild(anchor);
                  anchor.click();
                  document.body.removeChild(anchor);
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                } catch {
                  setExportError("Export failed. Please try again.");
                } finally {
                  setExportPending(false);
                }
              })();
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-brand-600 bg-brand-50 px-3 py-2 text-sm font-medium text-brand-700 transition hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {exportPending ? "Exporting…" : "Download Package Export"}
          </button>
        </div>
        {exportError ? (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {exportError}
          </p>
        ) : null}
        <p className="mt-2 text-xs text-slate-400">
          Format: Markdown (.md) with all stage documents bundled
        </p>
      </div>
    </div>
  );
}
